import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { FiArrowLeft, FiSearch, FiChevronDown, FiBookOpen, FiTruck, FiCreditCard, FiUser, FiHelpCircle } from 'react-icons/fi';
import './HelpCenter.css';

const FAQ_DATA = [
  {
    category: 'delivery',
    icon: <FiTruck />,
    categoryLabel: 'Shipping & Delivery',
    q: 'How fast is the delivery?',
    a: 'We offer fast delivery options across India. Standard shipping takes 3-5 business days, and state-wise premium shipping addon schedules are available during checkout.'
  },
  {
    category: 'delivery',
    icon: <FiTruck />,
    categoryLabel: 'Shipping & Delivery',
    q: 'Do you ship to my state?',
    a: 'Yes, Orbifert delivers to all states across India. You can select your state during checkout to see the applicable delivery options and specialized state-wise addons.'
  },
  {
    category: 'payments',
    icon: <FiCreditCard />,
    categoryLabel: 'Payments & Refunds',
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit and debit cards (Visa, Mastercard, Maestro, RuPay), UPI (PhonePe, Google Pay, Paytm), Net Banking, and Cash on Delivery (COD).'
  },
  {
    category: 'payments',
    icon: <FiCreditCard />,
    categoryLabel: 'Payments & Refunds',
    q: 'How can I get a refund for a returned product?',
    a: 'Refunds are processed to the original payment method within 5-7 business days after the returned items pass inspection at our organic storage center.'
  },
  {
    category: 'products',
    icon: <FiBookOpen />,
    categoryLabel: 'Product & Organic Farming',
    q: 'Are Orbifert bio-fertilizers 100% organic?',
    a: 'Yes! All Orbifert products are 100% chemical-free and certified by the NPOP (National Programme for Organic Production) and approved by ICAR.'
  },
  {
    category: 'products',
    icon: <FiBookOpen />,
    categoryLabel: 'Product & Organic Farming',
    q: 'How should I store liquid bio-fertilizers?',
    a: 'Store in a cool, dry place away from direct sunlight. Since they contain live beneficial microbes, keep the bottle tightly sealed and avoid mixing with synthetic chemical pesticides.'
  },
  {
    category: 'account',
    icon: <FiUser />,
    categoryLabel: 'Account & Orders',
    q: 'How do I track my active orders?',
    a: 'Go to your account dashboard under "Order History" or click the "Track Order" link in the footer. You will see real-time updates and active courier tracking links.'
  },
  {
    category: 'account',
    icon: <FiUser />,
    categoryLabel: 'Account & Orders',
    q: 'Can I change my order delivery address after ordering?',
    a: 'If the order has not been dispatched, you can update your shipping details by calling our direct helpline at +91 7228064418 or emailing orbifertorganic@gmail.com.'
  }
];

export default function HelpCenter({ onBack }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedIndex, setExpandedIndex] = useState(null);

  const containerRef = useRef(null);
  const cardsRef = useRef(null);
  const faqsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // Entrance animations
    tl.fromTo(containerRef.current.querySelector('.help-hero-block'), 
      { opacity: 0, y: -40 }, 
      { opacity: 1, y: 0, duration: 0.8 }
    );

    if (cardsRef.current) {
      tl.fromTo(cardsRef.current.children, 
        { scale: 0.9, opacity: 0 }, 
        { scale: 1, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'back.out(1.4)' },
        '-=0.4'
      );
    }

    if (faqsRef.current) {
      tl.fromTo(faqsRef.current.children, 
        { y: 30, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.08 },
        '-=0.3'
      );
    }
  }, []);

  const toggleFaq = (index) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }
  };

  // Filter FAQs based on category selection and search query
  const filteredFaqs = FAQ_DATA.filter((faq) => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.q.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          faq.a.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          faq.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="help-center-wrapper" ref={containerRef}>
      
      {/* Hero Banner with search bar */}
      <section className="help-hero-block">
        <div className="help-hero-bg-overlay" style={{ backgroundImage: "url('/banner/organic_fertilizer_hero.png')" }}></div>
        <div className="help-hero-tint"></div>
        <div className="container help-hero-inner">
          <button className="help-back-link" onClick={onBack}>
            <FiArrowLeft className="back-icon" /> Back to Shop
          </button>
          
          <span className="help-subtitle-tag">ORBIFERT HELP CENTER</span>
          <h1 className="help-main-title">How can we help you grow?</h1>
          
          <div className="help-search-box-wrap">
            <FiSearch className="search-bar-icon" />
            <input 
              type="text" 
              placeholder="Search questions, keywords, or topics..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="help-search-input"
            />
          </div>
        </div>
      </section>

      {/* Main Categories Section */}
      <section className="help-categories-section">
        <div className="container">
          <div className="help-categories-grid" ref={cardsRef}>
            <div 
              className={`help-cat-card ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => { setActiveCategory('all'); setExpandedIndex(null); }}
            >
              <div className="help-cat-icon"><FiHelpCircle /></div>
              <h3>Show All FAQs</h3>
              <p>Explore all questions and answers about our organic inputs and services.</p>
            </div>
            <div 
              className={`help-cat-card ${activeCategory === 'delivery' ? 'active' : ''}`}
              onClick={() => { setActiveCategory('delivery'); setExpandedIndex(null); }}
            >
              <div className="help-cat-icon"><FiTruck /></div>
              <h3>Shipping & Delivery</h3>
              <p>Rates, schedules, delivery addons, and selective state policies.</p>
            </div>
            <div 
              className={`help-cat-card ${activeCategory === 'payments' ? 'active' : ''}`}
              onClick={() => { setActiveCategory('payments'); setExpandedIndex(null); }}
            >
              <div className="help-cat-icon"><FiCreditCard /></div>
              <h3>Payments & Refunds</h3>
              <p>Accepted payment options, checkout questions, and returns processing.</p>
            </div>
            <div 
              className={`help-cat-card ${activeCategory === 'products' ? 'active' : ''}`}
              onClick={() => { setActiveCategory('products'); setExpandedIndex(null); }}
            >
              <div className="help-cat-icon"><FiBookOpen /></div>
              <h3>Product & Composting</h3>
              <p>Microbiology questions, NPOP certified soil tips, and agronomy advice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs List Section */}
      <section className="help-faqs-section">
        <div className="container">
          <div className="faq-section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Showing {filteredFaqs.length} results for category: <strong>{activeCategory.toUpperCase()}</strong></p>
          </div>

          <div className="faq-accordion-list" ref={faqsRef}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const isExpanded = expandedIndex === index;
                return (
                  <div key={index} className={`faq-accordion-item ${isExpanded ? 'open' : ''}`}>
                    <button className="faq-question-btn" onClick={() => toggleFaq(index)}>
                      <div className="question-left-wrap">
                        <span className="faq-item-icon">{faq.icon}</span>
                        <span className="faq-question-text">{faq.q}</span>
                      </div>
                      <FiChevronDown className="faq-chevron-icon" />
                    </button>
                    <div className="faq-answer-container">
                      <div className="faq-answer-inner">
                        <p>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="faq-empty-state">
                <span className="empty-state-icon">🔍</span>
                <h3>No FAQ Matches Found</h3>
                <p>Try searching using different keywords, or clear the search query to show all topics.</p>
                <button className="btn btn-secondary" onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}>
                  Reset Search Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Direct support CTA card */}
      <section className="help-support-contact-section">
        <div className="container">
          <div className="help-contact-banner">
            <div className="help-contact-left">
              <h2>Still have questions?</h2>
              <p>Our agronomists and support advisors are available to guide your organic transition.</p>
            </div>
            <div className="help-contact-right">
              <a href="mailto:orbifertorganic@gmail.com" className="btn btn-white-outline">Email Support</a>
              <a href="tel:+917228064418" className="btn btn-primary-solid">Call +91 7228064418</a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
